import { useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Dropdown from '@/components/molecules/dropdown/dropdown';
import ImageWithZoom from '@/components/molecules/image-with-zoom/image-with-zoom';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './slope-map.module.scss';

interface Map {
  id: string;
  image: Content.Properties.Image;
  maximumZoom?: number;
  name: string;
}

export interface SlopeMapProps extends ModuleWrapperProps {
  headline: string;
  maps: Map[];
}

const SlopeMap = ({ headline, maps = [], ...rest }: SlopeMapProps) => {
  const [activeMap, setActiveMap] = useState<Map>(maps[0]);
  const { t } = useI18n();

  const dropdownItems = maps.map(map => ({
    label: map.name,
    value: map.id,
  }));

  const handleChange = (id: string) => {
    const map = maps.find(map => map.id === id);

    if (!map) return;

    setActiveMap(map);
  };

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container>
        <Row className={styles.header}>
          <Column width={{ tablet: 6 }}>
            <Typography className={styles.headline} tagStyle="displayMedium">
              {headline}
            </Typography>
          </Column>

          {maps.length > 1 && (
            <Column width={{ tablet: 6 }}>
              <Dropdown
                className={styles.dropdown}
                items={dropdownItems}
                label={t('items.modules.slopeMap.chooseSlopeMap')}
                onChange={handleChange}
                value={activeMap.id}
              />
            </Column>
          )}
        </Row>

        {activeMap && (
          <Row>
            <Column className={styles.map}>
              <ImageWithZoom
                image={activeMap.image}
                maximumZoom={activeMap.maximumZoom}
              />
            </Column>
          </Row>
        )}
      </Container>
    </ModuleWrapper>
  );
};

SlopeMap.displayName = 'SlopeMap';

export default SlopeMap;
